import React, { useEffect, useState } from 'react'
import { Activity, Server, Settings, AlertTriangle, Ban } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'

interface SellerInfo { pop_ids: number[]; account: string; auction_time_limit?: number }

/**
 * Minimal operator landing page.
 *
 * Day-one scope (per requirements OPS-* + 04-system-stories.md):
 *   - Show the logged-in operator and their wallet
 *   - Show backend health (current auction window, whether the buyer Flask responds)
 *   - Surface the CLI commands the operator still needs (registerPoP,
 *     set_auction_time_limit, check_setup) rather than wiring buttons.
 *
 * Wiring of actual on-chain admin actions is deferred — the operator still
 * runs deploy/*.py from a terminal.
 */
export function OperatorLanding() {
  const { user } = useAuth()
  const [info,   setInfo]   = useState<SellerInfo | null>(null)
  const [err,    setErr]    = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    fetch('/host/info/', { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(setInfo)
      .catch(e => setErr(e?.message ?? String(e)))
      .finally(() => setLoaded(true))
  }, [])

  // The gateway forbids /host/* for operator role (role gate), so /host/info
  // will 403. That's fine — fall back to /version which is always proxied.
  const showHostInfo = info && !err

  return (
    <div style={{ maxWidth: 920, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0F172A', marginTop: 0 }}>
        Operator Console
      </h1>
      <p style={{ color: '#475569', fontSize: '0.85rem' }}>
        Welcome, <strong>{user?.username ?? user?.email}</strong>. This is the
        FIREx market operator landing. Most admin actions are still driven from
        the CLI; this page surfaces the current state of the deployment.
      </p>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '1rem', marginTop: '1.25rem',
      }}>
        <Card icon={<Server size={16} />} title="Identity">
          <KV k="Email"  v={user?.email ?? '—'} />
          <KV k="Wallet" v={"-"} mono />
          <KV k="Role"   v={user?.role ?? '—'} />
        </Card>

        <Card icon={<Activity size={16} />} title="Deployment">
          {!loaded && <span style={{ color: '#94A3B8', fontSize: '0.8rem' }}>loading…</span>}
          {loaded && showHostInfo && (
            <>
              <KV k="Backend" v={"-"} mono />
              <KV k="Auction window" v={`${info!.auction_time_limit ?? 300} s`} />
              <KV k="Account"        v={info!.account} mono />
            </>
          )}
          {loaded && !showHostInfo && (
            <span style={{ color: '#94A3B8', fontSize: '0.78rem' }}>
              Backend health unavailable from operator role.
              Use <code>python deploy/check_setup.py</code> from a terminal.
            </span>
          )}
        </Card>

        <Card icon={<Settings size={16} />} title="Admin actions (CLI)">
          <ul style={{ margin: 0, paddingLeft: '1.05rem', fontSize: '0.78rem', lineHeight: 1.7, color: '#334155' }}>
            <li><code>python deploy/check_setup.py</code> — verify deployment</li>
            <li><code>python deploy/register_ases.py</code> — register PoPs / regenerate contract details</li>
            <li><code>python deploy/seed_users.py</code> — add demo users</li>
            <li><code>python deploy/seed_users.py --reset</code> — wipe + re-seed users</li>
          </ul>
        </Card>
      </div>

      <CancelAuctionPanel />


      <div style={{
        marginTop: '1.5rem', padding: '0.75rem 1rem',
        background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '0.25rem',
        display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
        fontSize: '0.78rem', color: '#78350F',
      }}>
        <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Pilot caveat: this auth flow is for the demo. Production must replace it
          with proper identity (OIDC/SAML) and bind the on-chain wallet to a
          cryptographic proof rather than a database row.
        </span>
      </div>
    </div>
  )
}

// ─── Cancel-auction panel ─────────────────────────────────────────────────────
function CancelAuctionPanel() {
  const [rfqId,  setRfqId]  = useState('')
  const [reason, setReason] = useState('')
  const [busy,   setBusy]   = useState(false)
  const [msg,    setMsg]    = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const [confirming, setConfirming] = useState(false)

  const rfqIdNum  = Number(rfqId)
  const validRfq  = Number.isInteger(rfqIdNum) && rfqIdNum > 0
  const canSubmit = validRfq && !busy

  async function doCancel() {
    setBusy(true)
    setMsg(null)
    try {
      const r = await fetch(`/admin/cancel_auction/${rfqIdNum}`, {
        method:      'POST',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({ reason }),
      })
      const body = await r.json().catch(() => ({}))
      if (r.ok) {
        setMsg({ kind: 'ok', text: `RFQ ${rfqIdNum} cancelled. Status → FAILED.` })
        setRfqId(''); setReason(''); setConfirming(false)
      } else if (r.status === 409) {
        setMsg({ kind: 'err', text: body?.error ?? 'Auction not in a cancellable state.' })
      } else if (r.status === 403) {
        setMsg({ kind: 'err', text: 'Forbidden — operator role required.' })
      } else if (r.status === 502) {
        setMsg({ kind: 'err', text: `On-chain revert: ${body?.error ?? 'cancel_auction reverted'}` })
      } else {
        setMsg({ kind: 'err', text: body?.error ?? `Unexpected error (HTTP ${r.status})` })
      }
    } catch (e: any) {
      setMsg({ kind: 'err', text: e?.message ?? String(e) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{
      marginTop: '1.5rem', padding: '1rem 1.1rem',
      background: '#fff', border: '1px solid #E2E8F0', borderRadius: '0.4rem',
      boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.45rem',
        fontWeight: 600, fontSize: '0.85rem', color: '#0F172A',
        marginBottom: '0.6rem',
      }}>
        <Ban size={16} />
        Cancel auction
      </div>
      <p style={{ margin: 0, marginBottom: '0.7rem', color: '#475569', fontSize: '0.78rem', lineHeight: 1.55 }}>
        Force an in-flight auction to <strong>FAILED</strong>. Allowed only while the
        RFQ is <code>OPEN</code> or <code>CLOSED</code>; once cleared or settled the
        allocation is on-chain and cannot be revoked.
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div style={{ flex: '0 0 110px' }}>
          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#475569', marginBottom: 3 }}>
            RFQ ID
          </label>
          <input
            type="number" min={1}
            value={rfqId}
            onChange={e => { setRfqId(e.target.value); setMsg(null); setConfirming(false) }}
            placeholder="e.g. 7"
            style={{
              width: '100%', padding: '0.4rem 0.55rem',
              border: '1px solid #CBD5E1', borderRadius: '0.25rem',
              fontSize: '0.85rem', boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 220 }}>
          <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#475569', marginBottom: 3 }}>
            Reason (≤ 256 chars, optional)
          </label>
          <input
            type="text"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. duplicate of RFQ 6 / pricing error"
            maxLength={256}
            style={{
              width: '100%', padding: '0.4rem 0.55rem',
              border: '1px solid #CBD5E1', borderRadius: '0.25rem',
              fontSize: '0.85rem', boxSizing: 'border-box',
            }}
          />
        </div>
        {!confirming ? (
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => setConfirming(true)}
            style={{
              padding: '0.45rem 0.85rem', borderRadius: '0.25rem',
              fontSize: '0.78rem', fontWeight: 600,
              border: 'none', cursor: canSubmit ? 'pointer' : 'not-allowed',
              background: canSubmit ? '#DC2626' : '#94A3B8', color: '#fff',
              display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
            }}
          >
            <Ban size={13} />
            Cancel auction
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <button
              type="button"
              onClick={doCancel}
              disabled={busy}
              style={{
                padding: '0.45rem 0.85rem', borderRadius: '0.25rem',
                fontSize: '0.78rem', fontWeight: 600,
                border: 'none', cursor: busy ? 'wait' : 'pointer',
                background: '#B91C1C', color: '#fff',
              }}
            >
              {busy ? 'Cancelling…' : `Confirm cancel RFQ ${rfqIdNum}`}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={busy}
              style={{
                padding: '0.45rem 0.7rem', borderRadius: '0.25rem',
                fontSize: '0.78rem', fontWeight: 600,
                border: '1px solid #CBD5E1', cursor: 'pointer',
                background: '#fff', color: '#475569',
              }}
            >
              Back
            </button>
          </div>
        )}
      </div>

      {msg && (
        <div style={{
          marginTop: '0.75rem',
          padding: '0.5rem 0.65rem',
          fontSize: '0.78rem',
          borderRadius: '0.25rem',
          border: `1px solid ${msg.kind === 'ok' ? '#86EFAC' : '#FECACA'}`,
          background: msg.kind === 'ok' ? '#F0FDF4' : '#FEF2F2',
          color:      msg.kind === 'ok' ? '#166534' : '#991B1B',
        }}>
          {msg.text}
        </div>
      )}
    </div>
  )
}

// ─── Generic helpers ─────────────────────────────────────────────────────────
function Card({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #E2E8F0',
      borderRadius: '0.4rem',
      padding: '0.85rem 1rem',
      boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.4rem',
        fontWeight: 600, fontSize: '0.85rem', color: '#0F172A',
        marginBottom: '0.6rem',
      }}>
        {icon}
        {title}
      </div>
      {children}
    </div>
  )
}

function KV({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between',
      padding: '0.18rem 0', borderBottom: '1px dashed #F1F5F9',
      fontSize: '0.78rem',
    }}>
      <span style={{ color: '#64748B' }}>{k}</span>
      <span style={{
        color: '#0F172A',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, monospace' : undefined,
        textAlign: 'right', wordBreak: 'break-all',
      }}>
        {v}
      </span>
    </div>
  )
}
